import React, { useState, useCallback } from 'react';
import {
  Box, Card, Typography, TextField, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Tooltip, MenuItem,
} from '@mui/material';
import { Add, Refresh, Delete, ArrowBack } from '@mui/icons-material';
import { InvokeApi, apiList } from '../../services/ApiService';
import { Config } from '../../config/Config';
import ClassSectionGrid from '../../components/ClassSectionGrid';

export default function Exams() {
  const [view, setView] = useState('grid');
  const [selected, setSelected] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [examName, setExamName] = useState('');
  const [acYear, setAcYear] = useState('');
  const [acYears, setAcYears] = useState([]);

  const loadAcYears = async () => {
    const data = await InvokeApi(Config.main_url + apiList.getAvailableAcademicYears, {});
    const years = data?.academic_years || [];
    setAcYears(years);
    const current = years.find(y => y.is_current) || years[0];
    if (current) setAcYear(current.value || '');
  };

  const load = useCallback(async (cls) => {
    if (!cls) return;
    setLoading(true);
    const data = await InvokeApi(Config.main_url + apiList.getExams, {
      standard: cls.standard,
      section: cls.section,
    });
    setRecords(data?.exams || []);
    setLoading(false);
  }, []);

  const handleSelect = async (cls) => {
    setSelected(cls);
    setView('list');
    await loadAcYears();
    load(cls);
  };

  const handleSave = async () => {
    if (!examName.trim()) { alert('Exam name is required'); return; }
    setSaving(true);
    const data = await InvokeApi(Config.main_url + apiList.addExam, {
      exam: examName.trim(),
      standard: selected.standard,
      section: selected.section,
      ac_year: acYear,
      status: 'Active',
      createdBy: '1',
    });
    setSaving(false);
    if (data?.code === '200') {
      setDialog(false);
      setExamName('');
      load(selected);
    } else alert(data?.message || 'Failed');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this exam?')) return;
    await InvokeApi(Config.main_url + apiList.deleteExam, { id });
    load(selected);
  };

  if (view === 'grid') {
    return (
      <Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Exam Types</Typography>
          <Typography variant="body2" color="text.secondary">Select a class to manage exam types</Typography>
        </Box>
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', p: 3 }}>
          <ClassSectionGrid onSelect={handleSelect} />
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Exam Types</Typography>
          <Typography variant="body2" color="text.secondary">
            {selected?.standard} - {selected?.section} · {records.length} exams
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => setView('grid')}
            sx={{ borderRadius: 2, color: '#1a237e', borderColor: '#1a237e' }}>Back</Button>
          <Tooltip title="Refresh">
            <IconButton onClick={() => load(selected)} sx={{ border: '1px solid #e8eaf6', borderRadius: 2, color: '#1a237e' }}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<Add />} onClick={() => { setExamName(''); setDialog(true); }}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
            Add Exam
          </Button>
        </Box>
      </Box>

      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress sx={{ color: '#1a237e' }} />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ background: '#f5f6fa' }}>
                  {['#', 'Exam Name', 'Academic Year', 'Status', 'Actions'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {records.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                      No exams for {selected?.standard} - {selected?.section}
                    </TableCell>
                  </TableRow>
                ) : records.map((r, i) => (
                  <TableRow key={r.ID || i} hover>
                    <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{i + 1}</TableCell>
                    <TableCell sx={{ fontSize: 14, fontWeight: 600 }}>{r.exam}</TableCell>
                    <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>{r.ac_year || '—'}</TableCell>
                    <TableCell>
                      <Chip label={r.status || 'Active'} size="small"
                        sx={{ background: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }} />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDelete(r.ID)}
                          sx={{ color: '#c62828' }}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #f0f0f0' }}>
          Add Exam — {selected?.standard} - {selected?.section}
        </DialogTitle>
        <DialogContent sx={{ pt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField fullWidth size="small" label="Exam Name *" value={examName}
            onChange={e => setExamName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSave()}
            placeholder="e.g. Unit Test 1, Mid Term, Final Exam"
            autoFocus />
          {acYears.length > 0 && (
            <TextField select fullWidth size="small" label="Academic Year" value={acYear}
              onChange={e => setAcYear(e.target.value)}>
              {acYears.map(y => (
                <MenuItem key={y.value} value={y.value}>
                  {y.label || y.value}{y.is_current ? ' (Current)' : ''}
                </MenuItem>
              ))}
            </TextField>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={() => setDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
